
import React from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ItineraryEvent } from '@/types/itinerary';
import { useEditTransportation } from '@/hooks/travel/useEditTransportation';
import TransportTypePicker from './form/TransportTypePicker';
import UserNameField from './form/UserNameField';
import LocationFields from './form/LocationFields';
import FlightNumberField from './form/FlightNumberField';
import DepartureFields from './form/DepartureFields';
import ConfirmationSection from './form/ConfirmationSection';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface EditTransportationDialogProps {
  open: boolean;
  onClose: () => void;
  event: ItineraryEvent;
}

const EditTransportationDialog: React.FC<EditTransportationDialogProps> = ({
  open,
  onClose,
  event
}) => {
  const {
    details,
    handleChange,
    saveChanges,
    isSaving
  } = useEditTransportation(event, onClose);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveChanges();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[600px]">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Edit Transportation</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>X</Button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <TransportTypePicker 
            selectedType={details.type} 
            onChange={(value) => handleChange('type', value)} 
          />

          <UserNameField 
            userName={details.userName} 
            onChange={(value) => handleChange('userName', value)} 
          />

          <LocationFields 
            departure={details.departure}
            arrival={details.arrival}
            onDepartureChange={(value) => handleChange('departure', value)}
            onArrivalChange={(value) => handleChange('arrival', value)}
          />

          {details.type === 'flight' && (
            <FlightNumberField 
              flightNumber={details.flightNumber || ''}
              onChange={(value) => handleChange('flightNumber', value)}
            />
          )}

          <DepartureFields 
            departureDate={details.departureDate}
            departureTime={details.departureTime}
            onDateChange={(date) => handleChange('departureDate', date)}
            onTimeChange={(value) => handleChange('departureTime', value)}
          />

          <ConfirmationSection 
            confirmations={details.confirmations || []}
            onChange={handleChange}
          />

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Input 
              id="notes" 
              value={details.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Additional information"
            />
          </div>
          
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit"
              className="bg-glowingTeal hover:bg-navteal"
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditTransportationDialog;
